import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Img from '@/components/shared/Img';

const serviceLinks = [
  { label: 'Kitchen Remodeling', path: '/services/kitchen-remodeling' },
  { label: 'Bathroom Remodeling', path: '/services/bathroom-remodeling' },
  { label: 'Home Additions', path: '/services/home-additions' },
  { label: 'Custom Builds', path: '/services/custom-builds' },
  { label: 'Interior Finishes', path: '/services/interior-finishes' },
  { label: 'Flooring', path: '/services/flooring' },
  { label: 'Painting', path: '/services/painting' },
  { label: 'Roofing', path: '/services/roofing' },
  { label: 'Home Systems & Upgrades', path: '/services/home-systems-upgrades' },
  { label: 'EV Charger Installation', path: '/services/ev-charger-installation' },
  { label: 'ADA Compliance', path: '/services/ada-compliance' },
  { label: 'Light Commercial', path: '/services/commercial-remodeling' },
];

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services', hasDropdown: true },
  { label: 'Our Work', path: '/gallery' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {};
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0a0a0a] border-b border-white/10 py-3">
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <Img
            src="/images/33e6a8c4c_logowhitetext.jpg"
            alt="Terza Costa Construction"
            className="h-12 w-auto object-contain"
            sizes="200px"
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.path} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                  aria-controls="services-menu"
                  className={`flex items-center gap-1 text-sm font-body font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    location.pathname.startsWith('/services')
                      ? 'text-[#C97B2E]'
                      : 'text-white hover:text-[#C97B2E]'
                  }`}
                >
                  {link.label}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      id="services-menu"
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-[#0f0f0f] border border-white/10 rounded-sm shadow-2xl overflow-hidden z-50"
                    >
                      <Link
                        to="/services"
                        className="block px-5 py-3 font-body text-xs font-bold uppercase tracking-wider text-[#C97B2E] border-b border-white/10 hover:bg-white/5 transition-colors"
                      >
                        All Services
                      </Link>
                      {serviceLinks.map((s) => (
                        <Link
                          key={s.path}
                          to={s.path}
                          className="block px-5 py-3 font-body text-xs text-white/70 hover:text-white hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-body font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#C97B2E]'
                    : 'text-white hover:text-[#C97B2E]'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <a
            href="tel:2812037688"
            className="flex items-center gap-2 bg-[#C97B2E] text-[#0a0a0a] px-5 py-3 rounded-sm text-sm font-body font-extrabold uppercase tracking-wider hover:brightness-110 transition-colors duration-300"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            (281) 203-7688
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          className="lg:hidden text-white p-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C97B2E]"
        >
          {mobileOpen ? (
            <X className="w-6 h-6" aria-hidden="true" />
          ) : (
            <Menu className="w-6 h-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            id="mobile-menu"
            className="lg:hidden bg-[#0a0a0a] border-t border-white/10"
          >
            <div className="max-w-7xl mx-auto px-5 py-6 flex flex-col gap-4">
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div key={link.path}>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      aria-expanded={mobileServicesOpen}
                      aria-controls="mobile-services-menu"
                      className={`flex items-center gap-1 text-base font-body font-bold uppercase tracking-[0.15em] py-2 w-full ${
                        location.pathname.startsWith('/services') ? 'text-[#C97B2E]' : 'text-white'
                      }`}
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          id="mobile-services-menu"
                          className="pl-4 flex flex-col gap-2 overflow-hidden"
                        >
                          <Link to="/services" className="font-body text-xs font-bold uppercase tracking-wider text-[#C97B2E] py-1.5">All Services</Link>
                          {serviceLinks.map((s) => (
                            <Link key={s.path} to={s.path} className="font-body text-sm text-white/65 py-1.5">{s.label}</Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-base font-body font-bold uppercase tracking-[0.15em] py-2 ${
                      location.pathname === link.path ? 'text-[#C97B2E]' : 'text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <a
                href="tel:2812037688"
                className="flex items-center justify-center gap-2 bg-[#C97B2E] text-[#0a0a0a] px-5 py-3 rounded-sm text-sm font-body font-bold uppercase tracking-wider mt-2 hover:brightness-110 transition-colors"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                (281) 203-7688
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}